import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	FormlessField,
	Heading3,
	Pagination,
	Paragraph,
	Skeleton,
	Table,
} from '@treams/treams-ui';

export default function SentEmailsListContainer() {
	const { t } = useTranslation();
	const [emailInput, setEmailInput] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [isSuppressed, setIsSuppressed] = useState<boolean>(false);

	const [pagination, setPagination] = useState<Pagination>({ last: 10 });

	const { data, loading } = graphql.useSentEmailsQuery({
		variables: {
			email: email === '' ? undefined : email,
			...pagination,
		},
	});

	const [checkSuppressedEmail, { loading: suppressedLoading }] =
		graphql.useCheckSuppressedEmailMutation();

	const [removeSuppressedEmail, { loading: removeSuppressedEmailLoading }] =
		graphql.useRemoveSuppressedEmailMutation();

	const checkIfSuppressed = async (newEmail?: string) => {
		if (newEmail) {
			const response = await checkSuppressedEmail({
				variables: {
					input: {
						email: newEmail,
					},
				},
			});

			setIsSuppressed(response.data?.checkSuppressedEmail.isEmailSuppressed ?? false);
		} else {
			setIsSuppressed(false);
		}
	};

	const confirmSearch = async (event: React.FormEvent) => {
		event.preventDefault();

		if (loading || suppressedLoading) {
			return;
		}

		setEmail(emailInput);

		checkIfSuppressed(emailInput);
	};

	return (
		<Box>
			<Box mb={3}>
				<Heading3>{t('common.settings.sentEmails.title')}</Heading3>
			</Box>

			<Box mb={3}>
				<form onSubmit={confirmSearch}>
					<Flex justifyContent="space-between">
						<Box width={6 / 12}>
							<Flex>
								<Box width={9 / 12}>
									<FormlessField
										type="email"
										value={emailInput}
										onChange={(event) => {
											setEmailInput(event.currentTarget.value);
										}}
									/>
								</Box>
								<Box width={3 / 12} pl={3}>
									<input type="submit" style={{ display: 'none' }} />
									<Button
										disabled={loading || suppressedLoading}
										title="Search"
										onClick={confirmSearch}
									/>
								</Box>
							</Flex>
						</Box>
						<Box width={6 / 12} pl={3}>
							<Flex alignItems="center" justifyContent="flex-end">
								{email && !suppressedLoading && (
									<Box mr={3}>
										<Paragraph color={isSuppressed ? 'red' : 'slateBlue'}>
											User is{!isSuppressed ? ' not' : ''} on suppression list
										</Paragraph>
									</Box>
								)}
								<Button
									disabled={suppressedLoading || !isSuppressed || removeSuppressedEmailLoading}
									onClick={async () => {
										await removeSuppressedEmail({
											variables: {
												input: {
													email: email!,
												},
											},
										});

										setIsSuppressed(false);
									}}
									title="Remove"
								/>
							</Flex>
						</Box>
					</Flex>
				</form>
			</Box>

			<Table>
				<Table.Header>
					<Table.Cell basis={0.3}>Sent at</Table.Cell>
					<Table.Cell basis={0.3}>Email Address</Table.Cell>
					<Table.Cell basis={0.4}>Subject</Table.Cell>
				</Table.Header>
				{data?.sentEmails.edges
					.slice()
					.reverse()
					.map((edge) => (
						<Table.Row key={edge.cursor}>
							<Table.Cell basis={0.3}>
								{moment(edge.node.sentAt).format('YYYY-MM-DD HH:mm')}
							</Table.Cell>
							<Table.Cell basis={0.3}>{edge.node.recipientEmail}</Table.Cell>
							<Table.Cell basis={0.4}>{edge.node.subject}</Table.Cell>
						</Table.Row>
					)) ?? (
					<Skeleton.Repeat count={10}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.4} />
						</Table.Row>
					</Skeleton.Repeat>
				)}
			</Table>
			<Table.Paging
				pageInfo={data?.sentEmails.pageInfo as graphql.PageInfo}
				onPaginate={setPagination}
			/>
		</Box>
	);
}
